import { Color, Spacing } from '@persona-id/design-tokens';
import { StarFillIcon } from '@persona-id/design-tokens/icons';
import { ListIcon } from '@persona-id/design-tokens/icons/24px';
import IconLists from '@persona-id/design-tokens/icons/ListIcon';
import IconPlus from '@persona-id/design-tokens/icons/PlusIcon';
import { isEqual, uniqBy } from 'lodash/fp';
import { DateTime } from 'luxon';
import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom-v5-compat';
import { MEDIA_WIDTHS, mediaQuery } from 'shared/styles/variables';
import styled from 'styled-components';

import DisabledProductFeature from 'dashboard/components/DisabledProductFeature';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import MultiSelect from 'dashboard/components-lib/MultiSelect';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import TableLayout, {
  TableLinkTR,
  TableNullState,
  TableTD,
  TableTH,
} from 'dashboard/layouts/TableLayout';
import { ENHANCED_LIST_TYPES, LIST_TYPE_NAMES } from 'dashboard/lib/constants/list';
import { userDisplayName } from 'dashboard/lib/valueDisplayers/userDisplayName';
import { Environment } from 'dashboard/models/environment';
import { List, ListStatus } from 'dashboard/models/list';
import { Organization } from 'dashboard/models/organization';
import { User } from 'dashboard/models/user';
import { WorkflowObjectSchemas } from 'dashboard/models/workflow-schema';
import { ListsFilterParams } from 'dashboard/redux/actions/lists';
import { initialListsFilterParams } from 'dashboard/redux/reducers/lists';
import AddListModal, { AddListData } from 'dashboard/views/DashboardListsView/AddListModal';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Sticker, { StickerProps } from 'design-system/components/Sticker';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';

const LIST_STATUS_MAP: Record<ListStatus, StickerProps['variant']> = {
  [ListStatus.Active]: 'success',
  [ListStatus.Archived]: 'caution',
};

const LIST_STATUS_LABEL_MAP: Record<ListStatus, string> = {
  [ListStatus.Active]: 'Active',
  [ListStatus.Archived]: 'Inactive',
};

const STATUS_FILTER_OPTIONS = Object.values(ListStatus).map((listStatus) => ({
  label: LIST_STATUS_LABEL_MAP[listStatus],
  value: listStatus,
}));

const FilterRow = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-gap: ${Spacing[4]};

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
  }
`;

const StyledSticker = styled(Sticker)`
  height: 20px;
  margin-left: ${Spacing[4]};
`;

interface DashboardListsViewProps {
  currentEnvironment: Environment;
  featureIsDisabled: boolean;
  hasListsBasic: boolean;
  hasListsEnhanced: boolean;
  filters: ListsFilterParams;
  inquiryTemplateSchemas: WorkflowObjectSchemas['with-template'];
  isLoading: boolean;
  lists: List[];
  organization: Organization;
  users: User[];
  handleSubmitAddList: (values: AddListData) => Promise<void>;
  handleStatusFilterUpdate: (statuses: ListStatus[]) => void;
}

const DashboardListsView = (props: DashboardListsViewProps) => {
  const [addListModal, { enable: showAddListModal, disable: hideAddListModal }] = useToggle(false);
  const { status } = props.filters;

  const hasCreatePermission = usePermission(Permission.ListCreate);

  usePermission([Permission.ListWrite, Permission.ListCreate]);
  const location = useLocation();
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const openAddListModalParam = queryParams.get('openAddListModal');
    if (openAddListModalParam === 'true' && hasCreatePermission) {
      showAddListModal();
    }
  }, [location, hasCreatePermission, showAddListModal]);

  const statusFilterLabelTextMapper = useCallback((values: string[]) => {
    if (values.length === 1) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return STATUS_FILTER_OPTIONS.find((v) => v.value === values[0]).label;
    } else {
      return 'All Statuses';
    }
  }, []);

  const listRows = props.lists.map((list, key) => {
    const user =
      list.relationships.user.data != null
        ? (props.users.find((apiModel) => apiModel.id === list.relationships.user.data.id) as User)
        : undefined;

    const nameFull = userDisplayName(user);
    return (
      <ListTableRow
        list={list}
        listItemCount={list.attributes['list-item-count-active']}
        nameFull={nameFull}
        key={key}
      />
    );
  });

  // A filter is set by default so we check for equality rather than emptiness
  const hasUsedFilters = !isEqual(props.filters, initialListsFilterParams);

  const inquiryTemplateFieldOptions = uniqBy(
    'value',
    Object.values(props.inquiryTemplateSchemas || {}).flatMap((template) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return template.fields.config['item-schemas'].map((fieldSchema) => {
        return {
          label: fieldSchema.label,
          value: fieldSchema.key,
        };
      });
    }),
  );

  if (props.featureIsDisabled || ![props.hasListsBasic, props.hasListsEnhanced].some(Boolean)) {
    return (
      <DisabledProductFeature
        title='Lists'
        prompt='Create lists of information to easily flag inquiries that match information found on the list.'
        data-test='lists-view__show-null-state__null-state--no-filters'
        helpLink={
          <a
            href='https://support.withpersona.com/hc/en-us/articles/4410897998099-Lists-Overview'
            target='_blank'
            title='Learn more about Lists (Opens in a new window)'
            rel='noreferrer'
          >
            Learn more about Lists
          </a>
        }
        popupMessage='Your current plan does not have access to this feature.'
        icon={<IconLists />}
      />
    );
  }

  return (
    <>
      <ModalWrapper isOpen={addListModal} handleCloseModal={hideAddListModal}>
        <AddListModal
          hasListsEnhanced={props.hasListsEnhanced}
          inquiryTemplateFieldOptions={inquiryTemplateFieldOptions}
          handleClose={hideAddListModal}
          onSubmit={props.handleSubmitAddList}
          orgRetentionPeriodSeconds={props.organization.attributes['retention-period-seconds']}
        />
      </ModalWrapper>
      <InactiveEnvironmentBanner />
      <Container>
        <Header title='Lists'>
          {hasCreatePermission && (
            <Button
              id='lists-view__show-new-list'
              onClick={showAddListModal}
              width='auto'
              prefixIcon={<IconPlus />}
            >
              Add new list
            </Button>
          )}
        </Header>
        <Section>
          {(props.lists.length > 0 || hasUsedFilters) && (
            <Card>
              <Card.Body>
                <FilterRow id='lists-table__filters'>
                  <div>
                    <FormControlLabel>Status</FormControlLabel>
                    <MultiSelect
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      value={status}
                      selectOptionValues={STATUS_FILTER_OPTIONS}
                      labelTextMapper={statusFilterLabelTextMapper}
                      onChange={props.handleStatusFilterUpdate}
                      data-test='lists-view__select-status'
                      size='md'
                    />
                  </div>
                </FilterRow>
              </Card.Body>
            </Card>
          )}

          <Card>
            <Card.Body>
              <Card.Title>Lists</Card.Title>
              <TableLayout
                dataTest='lists-table__table'
                isLoading={props.isLoading}
                isEmpty={props.lists.length === 0}
                nullState={
                  <TableNullState
                    hasUsedFilters={hasUsedFilters}
                    dataTestBlock='lists-view__show-null-state'
                  >
                    <ListIcon size={64} color={Color.BorderIconSecondary} />
                    <Spacer height={3} />
                    <div>
                      Create lists of information to easily flag inquiries that match information
                      found on the list.
                    </div>
                    <Spacer height={5} />
                    <a
                      href='https://support.withpersona.com/hc/en-us/articles/4410897998099-Lists-Overview'
                      target='_blank'
                      title='Learn more about Lists (Opens in a new window)'
                      rel='noreferrer'
                    >
                      Learn more about Lists
                    </a>
                  </TableNullState>
                }
                headerTR={
                  <>
                    <TableTH>Name</TableTH>
                    <TableTH>Type</TableTH>
                    <TableTH># Items</TableTH>
                    <TableTH>Created by</TableTH>
                    <TableTH>{`Last updated at (${DateTime.local().toFormat(
                      TIMEZONE_NAME_ABBREVIATED,
                    )})`}</TableTH>
                    <TableTH>Status</TableTH>
                  </>
                }
              >
                {listRows}
                {/* TODO: PER-1572 list pagination */}
              </TableLayout>
            </Card.Body>
          </Card>
        </Section>
      </Container>
    </>
  );
};

export default DashboardListsView;

interface ListTableRowProps {
  list: List;
  listItemCount: string | undefined;
  nameFull: string | undefined;
}

const ListTableRow = ({ list, listItemCount, nameFull }: ListTableRowProps) => {
  const { name, 'updated-at': updatedAt, status } = list.attributes;
  return (
    <TableLinkTR
      to={`/dashboard/lists/${list.id}`}
      data-test={`list-table-row__${list.id}`}
      id={`list-table-row__${list.id}`}
    >
      <TableTD>
        {name}
        {ENHANCED_LIST_TYPES.includes(list.type) && (
          <StyledSticker variant='primary' color={Color.PrimaryBlack} icon={<StarFillIcon />}>
            Enhanced
          </StyledSticker>
        )}
      </TableTD>
      <TableTD>{LIST_TYPE_NAMES[list.type]}</TableTD>
      <TableTD>{listItemCount}</TableTD>
      <TableTD>{nameFull}</TableTD>
      <TableTD>{updatedAt && DateTime.fromISO(updatedAt).toFormat(DATE_AND_TIME_FORMAT)}</TableTD>
      <TableTD>
        {status && (
          <Sticker variant={LIST_STATUS_MAP[status]}>{LIST_STATUS_LABEL_MAP[status]}</Sticker>
        )}
      </TableTD>
    </TableLinkTR>
  );
};
